import React, { useEffect, useState, useCallback } from "react";
import NavBar from "@/components/NavBar";
import RideMap from "@/components/RideMap";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

export default function DriverDashboard() {
  const { user, refresh } = useAuth();
  const [online, setOnline] = useState(user?.is_online || false);
  const [available, setAvailable] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sectors, setSectors] = useState([]);
  const [demand, setDemand] = useState({});
  const [showHeat, setShowHeat] = useState(true);

  const refreshAvail = useCallback(async () => {
    try { const { data } = await api.get("/rides/available"); setAvailable(data || []); } catch {}
    try { const { data } = await api.get("/rides/active"); setActiveRide(data || null); } catch {}
  }, []);

  useEffect(() => {
    refreshAvail();
    const t = setInterval(refreshAvail, 4000);
    return () => clearInterval(t);
  }, [refreshAvail]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sectors");
        setSectors(data || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const tick = async () => {
      try { const { data } = await api.get("/sectors/demand"); setDemand(data || {}); } catch {}
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => clearInterval(t);
  }, []);

  const toggleOnline = async () => {
    const next = !online;
    setOnline(next);
    let coords = null;
    if (navigator.geolocation && next) {
      coords = await new Promise((res) => navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => res(null)
      ));
      if (coords) setMyLoc(coords);
    }
    try {
      await api.post("/driver/status", { is_online: next, lat: coords?.lat, lng: coords?.lng });
      toast.success(next ? "Vous êtes EN LIGNE" : "Vous êtes HORS LIGNE");
      refresh();
    } catch { toast.error("Échec de la mise à jour du statut"); }
  };

  const accept = async (rideId) => {
    try { const { data } = await api.post(`/rides/${rideId}/accept`); setActiveRide(data); toast.success("Course acceptée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec de l'acceptation"); }
  };

  const start = async () => {
    try { const { data } = await api.post(`/rides/${activeRide.ride_id}/start`); setActiveRide(data); }
    catch { toast.error("Échec du démarrage"); }
  };

  const complete = async () => {
    try { const { data } = await api.post(`/rides/${activeRide.ride_id}/complete`); setActiveRide(data); toast.success("Course terminée"); }
    catch { toast.error("Échec de la finalisation"); }
  };

  const release = async () => {
    if (!activeRide) return;
    if (!window.confirm("Annuler cette course planifiée ? (préavis 24h requis)")) return;
    try {
      await api.post(`/rides/${activeRide.ride_id}/driver-release`);
      setActiveRide(null);
      toast.success("Course remise en file pour un autre chauffeur");
    } catch (e) { toast.error(e.response?.data?.detail || "Annulation impossible"); }
  };

  const isScheduledAccepted = activeRide?.status === "accepted" && activeRide?.scheduled_for;
  const canRelease = (() => {
    if (!isScheduledAccepted) return false;
    const sched = new Date(activeRide.scheduled_for).getTime();
    return sched - Date.now() >= 24 * 3600 * 1000;
  })();

  return (
    <div className="min-h-screen bg-white">
      <NavBar>
        <button data-testid="driver-online-toggle" onClick={toggleOnline} className={`brut-btn ${online ? "" : "brut-btn-secondary"}`}>
          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${online ? "bg-[#00C853] pulse-yellow" : "bg-gray-400"}`} />
          {online ? "EN LIGNE" : "HORS LIGNE"}
        </button>
      </NavBar>

      <div className="grid lg:grid-cols-12 gap-0 min-h-[calc(100vh-64px)]">
        <aside className="lg:col-span-4 border-r-2 border-black p-6 bg-[#F4F4F5] overflow-y-auto" data-testid="driver-panel">
          <p className="font-mono-eyebrow">// Chauffeur</p>
          <h1 className="font-display text-3xl mt-1">{activeRide ? "COURSE ACTIVE" : "DEMANDES"}</h1>

          {activeRide ? (
            <div className="mt-6 brut-card p-4" data-testid="driver-active-ride">
              <p className="font-mono-eyebrow">// {(({pending: "EN ATTENTE", accepted: "ACCEPTÉE", in_progress: "EN COURS", completed: "TERMINÉE"})[activeRide.status] || activeRide.status?.toUpperCase())}</p>
              <p className="font-display text-2xl mt-1">{activeRide.passenger_name}</p>
              <p className="font-mono-eyebrow mt-3">Départ</p>
              <p className="text-sm">{activeRide.pickup?.address}</p>
              <p className="font-mono-eyebrow mt-3">Destination</p>
              <p className="text-sm">{activeRide.destination?.address}</p>
              <div className="flex justify-between mt-3 pt-3 border-t-2 border-black">
                <span className="font-mono-eyebrow">{activeRide.distance_km}KM</span>
                <span className="font-display text-xl">{fmt(activeRide.fare)}</span>
              </div>
              <div className="mt-4 space-y-2">
                {activeRide.status === "accepted" && !activeRide.scheduled_for && (
                  <button data-testid="driver-start-btn" onClick={start} className="brut-btn w-full">Démarrer la course →</button>
                )}
                {activeRide.status === "accepted" && activeRide.scheduled_for && (
                  <>
                    <p className="text-xs text-[#52525B]">Course planifiée. Vous pourrez la démarrer le jour J.</p>
                    {canRelease ? (
                      <button data-testid="driver-release-btn" onClick={release} className="brut-btn brut-btn-secondary w-full" style={{background:"#FF3B30",color:"white"}}>Annuler (≥24h)</button>
                    ) : (
                      <p className="text-[10px] text-[#52525B]">L'annulation chauffeur n'est plus possible (moins de 24h avant la course).</p>
                    )}
                  </>
                )}
                {activeRide.status === "in_progress" && (
                  <button data-testid="driver-complete-btn" onClick={complete} className="brut-btn w-full">Terminer la course →</button>
                )}
              </div>
            </div>
          ) : (
            <>
              {!online && (
                <div className="mt-6 p-4 border-2 border-black bg-white">
                  <p className="font-mono-eyebrow">// Non visible</p>
                  <p className="text-sm mt-2">Passez EN LIGNE pour recevoir des demandes de course.</p>
                </div>
              )}
              {online && available.length === 0 && (
                <div className="mt-6 p-4 border-2 border-black bg-white">
                  <p className="font-mono-eyebrow overflow-hidden">// En attente</p>
                  <p className="font-display text-xl mt-2">PAS DE DEMANDE</p>
                  <p className="text-sm mt-2 text-[#52525B]">Patientez, les demandes apparaissent ici en temps réel.</p>
                </div>
              )}
              {online && available.length > 0 && (
                <div className="mt-4 flex gap-2" data-testid="ride-filters">
                  {[
                    { id: "all", label: "Toutes" },
                    { id: "today", label: "Aujourd'hui" },
                    { id: "scheduled", label: "Planifiées" },
                  ].map(f => (
                    <button
                      key={f.id}
                      data-testid={`filter-${f.id}`}
                      onClick={() => setFilter(f.id)}
                      className={`border-2 border-black px-3 py-1 font-mono-eyebrow text-xs uppercase ${filter === f.id ? "bg-[#FFD600]" : "bg-white"}`}
                    >{f.label}</button>
                  ))}
                </div>
              )}
              <div className="mt-6 space-y-3">
                {available.filter(r => {
                  if (filter === "scheduled") return r.status === "scheduled";
                  if (filter === "today") {
                    const ref = r.scheduled_for || r.created_at;
                    if (!ref) return false;
                    const d = new Date(ref);
                    const now = new Date();
                    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
                  }
                  return true;
                }).map((r) => (
                  <div key={r.ride_id} className="brut-card p-4" data-testid={`available-ride-${r.ride_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono-eyebrow">
                          {r.vehicle_type?.toUpperCase()}
                          {r.status === "scheduled" && <span className="ml-2 brut-badge" style={{background:"#0A0A0A",color:"#FFD600"}}>PLANIFIÉE</span>}
                        </p>
                        <p className="font-display text-lg">{r.passenger_name}</p>
                      </div>
                      <span className="font-display text-2xl">{fmt(r.fare)}</span>
                    </div>
                    {r.scheduled_for && (
                      <p className="text-xs mt-1 font-mono-eyebrow">Pour : {new Date(r.scheduled_for).toLocaleString("fr-CH")}</p>
                    )}
                    <p className="text-xs mt-2">De : {r.pickup?.address?.slice(0, 50)}...</p>
                    <p className="text-xs">À : {r.destination?.address?.slice(0, 50)}...</p>
                    <button data-testid={`accept-ride-${r.ride_id}`} onClick={() => accept(r.ride_id)} className="brut-btn w-full mt-3">Accepter →</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </aside>
        <main className="lg:col-span-8 p-4 relative">
          <div className="absolute top-6 right-6 z-[400] bg-white border-2 border-black shadow-[4px_4px_0_0_#0A0A0A]">
            <button
              data-testid="toggle-heatmap-btn"
              onClick={() => setShowHeat(s => !s)}
              className="px-3 py-2 font-mono-eyebrow text-xs"
            >{showHeat ? "▣ HEATMAP ON" : "▢ HEATMAP OFF"}</button>
          </div>
          <RideMap
            center={myLoc ? [myLoc.lat, myLoc.lng] : [46.2044, 6.1432]}
            pickup={activeRide?.pickup}
            destination={activeRide?.destination}
            route={activeRide ? [[activeRide.pickup.lat, activeRide.pickup.lng], [activeRide.destination.lat, activeRide.destination.lng]] : null}
            sectors={showHeat ? sectors : []}
            demand={showHeat ? demand : {}}
            height="calc(100vh - 120px)"
          />
        </main>
      </div>
    </div>
  );
}
